// Timezone utilities for date/time handling

export interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
  group: string;
}

// Common timezones organized by region
export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  // UTC
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', offset: '+00:00', group: 'UTC' },
  
  // North America
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: '-05:00/-04:00', group: 'North America' },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: '-06:00/-05:00', group: 'North America' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: '-07:00/-06:00', group: 'North America' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: '-08:00/-07:00', group: 'North America' },
  { value: 'America/Toronto', label: 'Toronto', offset: '-05:00/-04:00', group: 'North America' },
  { value: 'America/Vancouver', label: 'Vancouver', offset: '-08:00/-07:00', group: 'North America' },
  { value: 'America/Mexico_City', label: 'Mexico City', offset: '-06:00/-05:00', group: 'North America' },
  
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)', offset: '+00:00/+01:00', group: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', offset: '+01:00/+02:00', group: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', offset: '+01:00/+02:00', group: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)', offset: '+01:00/+02:00', group: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)', offset: '+01:00/+02:00', group: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)', offset: '+01:00/+02:00', group: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)', offset: '+01:00/+02:00', group: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)', offset: '+03:00', group: 'Europe' },
  
  // Asia
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: '+09:00', group: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', offset: '+08:00', group: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', offset: '+08:00', group: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', offset: '+08:00', group: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', offset: '+09:00', group: 'Asia' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Delhi (IST)', offset: '+05:30', group: 'Asia' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)', offset: '+04:00', group: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)', offset: '+07:00', group: 'Asia' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Cairo (EET)', offset: '+02:00', group: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)', offset: '+02:00', group: 'Africa' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)', offset: '+01:00', group: 'Africa' },
  { value: 'Africa/Nairobi', label: 'Nairobi (EAT)', offset: '+03:00', group: 'Africa' },
  
  // Australia/Oceania
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)', offset: '+10:00/+11:00', group: 'Australia/Oceania' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)', offset: '+10:00/+11:00', group: 'Australia/Oceania' },
  { value: 'Australia/Perth', label: 'Perth (AWST)', offset: '+08:00', group: 'Australia/Oceania' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)', offset: '+12:00/+13:00', group: 'Australia/Oceania' },
  
  // South America
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', offset: '-03:00', group: 'South America' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (ART)', offset: '-03:00', group: 'South America' },
  { value: 'America/Santiago', label: 'Santiago (CLT)', offset: '-04:00/-03:00', group: 'South America' },
];

// Get timezone options grouped by region
export const getTimezoneOptionsByGroup = (): Record<string, TimezoneOption[]> => {
  return TIMEZONE_OPTIONS.reduce((groups, timezone) => {
    if (!groups[timezone.group]) {
      groups[timezone.group] = [];
    }
    groups[timezone.group].push(timezone);
    return groups;
  }, {} as Record<string, TimezoneOption[]>);
};

// Get user's local timezone
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Format date/time in specific timezone
export const formatInTimezone = (
  date: Date,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    ...options
  };

  return new Intl.DateTimeFormat('en-CA', defaultOptions).format(date);
};

// Convert date to specific timezone
export const convertToTimezone = (date: Date, timezone: string): Date => {
  // Get the date in the target timezone
  const utc = date.getTime() + (date.getTimezoneOffset() * 60000);
  const targetTime = new Date(utc + (getTimezoneOffset(timezone) * 60000));
  return targetTime;
};

// Get timezone offset in minutes
export const getTimezoneOffset = (timezone: string): number => {
  const now = new Date();
  const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
  const targetTime = new Date(utc.toLocaleString('en-US', { timeZone: timezone }));
  return (targetTime.getTime() - utc.getTime()) / 60000;
};

// Check if timezone is valid
export const isValidTimezone = (timezone: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

// Get timezone display name
export const getTimezoneDisplayName = (timezone: string): string => {
  const option = TIMEZONE_OPTIONS.find(tz => tz.value === timezone);
  return option ? option.label : timezone;
};

// Convert date string to timezone-aware date
export const parseDateInTimezone = (dateString: string, timezone: string): Date => {
  // Parse the date string as if it's in the specified timezone
  const date = new Date(dateString);
  
  // Adjust for timezone offset
  const offset = getTimezoneOffset(timezone);
  const utc = date.getTime() - (offset * 60000);
  
  return new Date(utc);
};

// Format date for storage (always UTC)
export const formatDateForStorage = (date: Date, timezone?: string): string => {
  if (timezone) {
    // Convert to UTC for storage
    return date.toISOString();
  }
  return date.toISOString();
};

// Parse stored date with timezone consideration
export const parseStoredDate = (dateString: string, timezone?: string): Date => {
  const date = new Date(dateString);
  
  if (timezone) {
    // The date is stored in UTC, convert to the specified timezone for display
    return convertToTimezone(date, timezone);
  }
  
  return date;
};
